import { keyBy, isEqual } from 'lodash'
import * as Flows from './types.no-deps'
import type * as Guide from '../../../shared/domain/guides/types.no-deps'
import { sortChronologicallyDescending, getConflicts, answerIsEmpty, getAnswerLoopPath } from './answer.no-deps'
import { evaluateCalculation, calcutionTimestamp } from './evaluateCalculation.no-deps'
import { hasInputs, hasShowIfs } from './typeGuards.no-deps'
import { type Dictionary, dictPick } from '../../lib/functional/dictionary.no-deps'
import { getAllElements } from './element.no-deps'
import { SummaryBuilder } from './SummaryBuilder.no-deps'
import { validateAnswer } from './validateAnswer.no-deps'
import { evaluateCondition, evaluateUnitDataCondition } from './evaluateCondition.no-deps'
import { pipe } from '../../lib/functional/pipe.no-deps'
import { filter, every, map, isEmpty } from '../../lib/functional/array.no-deps'
import { not } from '../../lib/functional/boolean.no-deps'
import type { TFullUnit, UnitData } from '../units/types.no-deps'
import { type LoopPath, type LoopPathIds, toLoopPath } from './LoopPath.no-deps'
import { getUnitIdsFromLoopPlan, type LoopPlan } from './LoopPlan.no-deps'
import { type CachedUnitData, getDataForUnit } from '../units/CachedUnitData.no-deps'
import { getElementToStepLookup } from './step.no-deps'
import type { WorkflowReference } from '../../../shared/domain/workflows/types.no-deps'
import type { ParentIsUserSelectable, UnitFilter } from './UnitFilter.no-deps'
import { includesAllMembers } from '../../lib/array.no-deps'
import { isNotNil, isNil } from '../../lib/functional/nil.no-deps'
import type { UUID } from '../../lib/typeHelpers.no-deps'

type Element = Flows.Element | Flows.NestedElement | Flows.Section
type Unit = Pick<TFullUnit, 'id' | 'parents'>

export interface Lookup {
    sectionId: Flows.ElementId
    steps: Flows.Step[]
    elements: Dictionary<Flows.Element | Flows.NestedElement>
    allAnswers: Flows.Answer[]
    reportStartDate: Date
    subjectUnitData: UnitData
    isSubtaskOpen: (reportId: Flows.ReportId) => boolean
    loopPlan: LoopPlan
    cachedUnitData: CachedUnitData
    allUnitsRelevantForFlow: Unit[]
    reportCompletionDate?: Date
    elementToStepLookup: Map<Flows.ElementId, Flows.Step>
    requiredGuidesMap: Map<Guide.Id, Pick<Guide.SyncGuide, 'isDeleted'>>
    guideElementsContextMap: Map<Guide.Id, { guide: Guide.SyncGuide; workflow: WorkflowReference }>
}

export function getFlowSummary(
    flow: Flows.Flow,
    allAnswers: Flows.Answer[],
    reportStartDate: Date,
    subjectUnitData: UnitData,
    isSubtaskOpen: (reportId: Flows.ReportId) => boolean,
    loopPlan: LoopPlan,
    allUnitsRelevantForFlow: Unit[],
    cachedUnitData: CachedUnitData,
    requiredGuidesMap: Map<Guide.Id, Pick<Guide.SyncGuide, 'isDeleted'>>,
    guideElementsContextMap: Map<Guide.Id, { guide: Guide.SyncGuide; workflow: WorkflowReference }>,
    reportCompletionDate?: Date
): Flows.FlowSummary {
    return getSectionSummary(
        flow,
        allAnswers,
        reportStartDate,
        subjectUnitData,
        isSubtaskOpen,
        loopPlan,
        [],
        allUnitsRelevantForFlow,
        cachedUnitData,
        requiredGuidesMap,
        guideElementsContextMap,
        reportCompletionDate
    )
}

export function getSectionSummary(
    section: Flows.Section,
    allAnswers: Flows.Answer[],
    reportStartDate: Date,
    subjectUnitData: UnitData,
    isSubtaskOpen: (reportId: Flows.ReportId) => boolean,
    loopPlan: LoopPlan,
    loopPathIds: LoopPathIds,
    allUnitsRelevantForFlow: Unit[],
    cachedUnitData: CachedUnitData,
    requiredGuidesMap: Map<Guide.Id, Pick<Guide.SyncGuide, 'isDeleted'>>,
    guideElementsContextMap: Map<Guide.Id, { guide: Guide.SyncGuide; workflow: WorkflowReference }>,
    reportCompletionDate?: Date
): Flows.SectionSummary {
    allAnswers = sortChronologicallyDescending(allAnswers)
    loopPlan = loopPlan ?? []

    // Create an OO style builder
    const builder = new SummaryBuilder(section.id)

    // Put the arguments so we can look them up somewhere down the tree
    const lookup: Lookup = {
        sectionId: section.id,
        steps: section.steps,
        elements: keyBy(getAllElements(section), 'id'),
        allAnswers,
        allUnitsRelevantForFlow,
        reportStartDate,
        subjectUnitData,
        isSubtaskOpen,
        loopPlan,
        cachedUnitData,
        reportCompletionDate,
        elementToStepLookup: getElementToStepLookup(section),
        requiredGuidesMap,
        guideElementsContextMap,
    }

    // Find all conflicting answers
    // Conflict is when two different contributors (user who put an answer) put different answer
    // while they were working on the same report
    builder.addConflicts(getConflicts(allAnswers))

    // Find all contributors
    for (const answer of allAnswers) builder.addContributor(answer.user)

    //
    // Steps

    const firstStep = section.steps[0]
    if (firstStep == null) return builder.summary
    handleStep(builder, lookup, firstStep, loopPathIds)

    //
    // Handle section completion

    const sectionAnswer = getAnswerToSelect(allAnswers, section, loopPathIds)

    if (
        sectionAnswer &&
        sectionAnswer.value === 'completed' &&
        builder.summary.answers.every((a) => a.timestamp <= sectionAnswer.timestamp)
    ) {
        builder.addAnswer(sectionAnswer)
        builder.setCompleted(sectionAnswer)
    }

    return builder.summary
}

function routeToStep(builder: SummaryBuilder, lookup: Lookup, id: Flows.StepId, loopPathIds: LoopPathIds) {
    const nextStep = findStep(lookup.steps, id)
    if (nextStep == null) {
        throw new Error("Can't find step to route to")
    }
    handleStep(builder, lookup, nextStep, loopPathIds)
}

function findNextStep(steps: Flows.Step[], id: Flows.StepId) {
    const index = findStepIndex(steps, id)
    if (index === -1) return null
    return steps[index + 1]
}

function handleStep(builder: SummaryBuilder, lookup: Lookup, step: Flows.Step, loopPathIds: LoopPathIds) {
    if (!Flows.isConcreteStep(step)) {
        return
    }

    if (builder.hasVisitedStep(step, loopPathIds)) throw new Error('Have already visited step: ' + step.id)
    builder.addStep(step, loopPathIds)

    for (const element of step.elements) {
        handleElement(builder, lookup, element, loopPathIds)
    }

    for (const conditionalRoute of step.conditionalRoutes) {
        let shouldRoute = false
        if (Flows.isElementConditionalRoute(conditionalRoute)) {
            const { elementId } = conditionalRoute
            shouldRoute = evaluateCondition(
                builder.summary,
                conditionalRoute.condition,
                elementId,
                loopPathIds,
                dictPick(lookup.elements, getElementIdsForCalculation(builder.summary, loopPathIds)),
                lookup.subjectUnitData,
                lookup.elementToStepLookup
            )
        }

        if (Flows.isUnitDataConditionalRoute(conditionalRoute)) {
            shouldRoute = evaluateUnitDataCondition(conditionalRoute, lookup.subjectUnitData)
        }

        if (!shouldRoute) continue
        if (conditionalRoute.routeTo === 'ConditionalRoute.EndOfSection') return
        routeToStep(builder, lookup, conditionalRoute.routeTo, loopPathIds)

        return
    }

    switch (step.defaultRoute) {
        case 'Step.EndOfSection':
            return
        case 'Step.NextStep':
            const nextStep = findNextStep(lookup.steps, step.id)
            if (nextStep != null) handleStep(builder, lookup, nextStep, loopPathIds)
            return
        default:
            routeToStep(builder, lookup, step.defaultRoute, loopPathIds)
    }
}

function handleElement(
    builder: SummaryBuilder,
    lookup: Lookup,
    element: Flows.Element | Flows.NestedElement,
    loopPathIds: LoopPathIds
) {
    builder.addElement(element, loopPathIds)

    // Sections
    if (element.type === 'section') {
        const subSectionSummary = getSectionSummary(
            element,
            lookup.allAnswers,
            lookup.reportStartDate,
            lookup.subjectUnitData,
            lookup.isSubtaskOpen,
            lookup.loopPlan,
            loopPathIds,
            lookup.allUnitsRelevantForFlow,
            lookup.cachedUnitData,
            lookup.requiredGuidesMap,
            lookup.guideElementsContextMap,
            lookup.reportCompletionDate
        )
        const isComplete = subSectionSummary.completedDate != null
        const isInProgress = getInProgressState(subSectionSummary)
        const ignoreSubsectionFailures = !element.required && !isInProgress

        builder.addSteps(subSectionSummary.steps)
        builder.addElements(subSectionSummary.elements)
        builder.addAnswers(subSectionSummary.answers)
        builder.addCalculations(subSectionSummary.calculations)

        if (!ignoreSubsectionFailures) {
            builder.addFailures(subSectionSummary.validationFailures)
        }

        const reportEditMode = lookup.reportCompletionDate ?? false

        const sectionAnswersNotEdited = subSectionSummary.answers.every(
            (answer) => answer.timestamp.getTime() < lookup.reportCompletionDate?.getTime()
        )

        // We don't want to validate section answers if we are in edit mode and
        // no answers submitted for this section are after the report completed date
        const noValidationForSection = reportEditMode && sectionAnswersNotEdited

        if (noValidationForSection) {
            builder.setInProgressState(element, loopPathIds, false)
            return
        }

        if (isComplete && hasFailures(subSectionSummary, loopPathIds) && !ignoreSubsectionFailures) {
            builder.addFailure(element.id, loopPathIds, 'invalidSection')
        }
        if (!isComplete && element.required) {
            builder.addFailure(element.id, loopPathIds, 'required')
        }

        if (!isComplete) {
            builder.setInProgressState(element, loopPathIds, isInProgress)
        }

        return
    }

    if (element.type === 'loop') {
        // unit filters that need selecting by the user in the UI need to be added back so they can
        // be rendered in the selects
        let selectedUnitsForFiltering: number[][] = []
        let areAllFiltersSelected = true
        if (element.unitFilters) {
            const loopPath = toLoopPath(loopPathIds)

            // if there is filter by parameter but there is no parameter value defined for it, then treat is as UserSelectable
            const unitFilters = element.unitFilters.map((filter) => {
                if (filter.type === 'ParentFromParameter') {
                    return {
                        ...filter,
                        id: filter.parameterId as UUID,
                        type: 'ParentIsUserSelectable' as const,
                    }
                }
                return filter
            })
            const userSelectableFilters = unitFilters.filter((filter) => filter.type === 'ParentIsUserSelectable')
            const unitSelectFilterAnswers = userSelectableFilters.map((filter: ParentIsUserSelectable) => {
                const elementId = filter.id as Flows.ElementId
                // we are sorting chronologically desc and do slice(0,1) because we want only the latest answer given for given element
                return [
                    elementId,
                    sortChronologicallyDescending(
                        getElementAnswers(lookup.allAnswers, elementId, '' as LoopPath)
                    ).slice(0, 1),
                ]
            })
            unitSelectFilterAnswers.forEach(([elementId, givenAnswersToFilter]: [Flows.ElementId, Flows.Answer[]]) => {
                if (
                    isEmpty(givenAnswersToFilter) ||
                    givenAnswersToFilter.some((answer) =>
                        answer.type === 'unit-select' ? answer.value === null : false
                    )
                ) {
                    builder.addFailure(elementId, [], 'required')
                } else {
                    builder.addAllAnswers(givenAnswersToFilter)
                }
            })

            selectedUnitsForFiltering = unitSelectFilterAnswers
                .flatMap(([_, answers]: [Flows.ElementId, Flows.Answer[]]) =>
                    answers.filter((answer) => answer.type === 'unit-select' && answer.value !== null)
                )
                .map((answer) => answer.value as number[])
            areAllFiltersSelected = selectedUnitsForFiltering.length === userSelectableFilters.length

            /* add all other answers related to element but outside of loop, for choices relating to filtering */
            const elementAnswers = getElementAnswers(lookup.allAnswers, element.id, loopPath)
            builder.addAllAnswers(elementAnswers)
        }

        // if ParentIsUserSelectable unitFilters are present then we need to filter units in content loop with selected units
        // but if ParentIsUserSelectable unitFilters are present but units are not selected for these filters then we don't want to handle any of the step for
        // units in loop
        const loopBreadthIds =
            element.unitFilters.filter((filter) => filter.type === 'ParentIsUserSelectable').length > 0
                ? areAllFiltersSelected
                    ? getUnitIdsFromLoopPlan(lookup.loopPlan, element.id)
                          .map((loopId) => lookup.allUnitsRelevantForFlow.find((unit) => unit.id === loopId.unitId))
                          .filter(isNotNil)
                          .filter((unit) => {
                              return includesAllMembers(unit.parents, selectedUnitsForFiltering.flat())
                          })
                          .map((unit) => ({ unitId: unit.id }))
                    : []
                : getUnitIdsFromLoopPlan(lookup.loopPlan, element.id)
        for (const loopId of loopBreadthIds) {
            const newLoopIds = [...loopPathIds, loopId]
            const newSubjectUnitData = getDataForUnit(lookup.cachedUnitData, loopId.unitId)
            const newLookUp: Lookup = { ...lookup, steps: element.steps, subjectUnitData: newSubjectUnitData }
            handleStep(builder, newLookUp, element.steps[0], newLoopIds)
        }
        return
    }

    // If the element.type === 'guide' and is required but the guide has been deleted
    // return without doing any validation for this element
    if (element.type === 'guide' && element?.required && lookup.requiredGuidesMap.get(element.guideId)?.isDeleted) {
        return
    }

    // Value will either be the value of the answer selected or
    // the result of evaluating a calculation
    let showIfValue: number | Flows.Answer['value']

    // If element has any inputs for giving answers
    if (hasInputs(element)) {
        // Pick answer
        const answer = getAnswerToSelect(lookup.allAnswers, element, loopPathIds)
        builder.addAnswer(answer)

        // Validate answer
        const failures = validateAnswer(
            answer,
            lookup.sectionId,
            element,
            lookup.reportStartDate,
            lookup.isSubtaskOpen,
            loopPathIds,
            lookup.guideElementsContextMap
        )

        builder.addFailures(failures)

        if (answer == null) return

        // Set value for show if
        showIfValue = answer.value
    }

    // Evaluate calculation
    if (element.type === 'calculation') {
        const relevantElementIds = getElementIdsForCalculation(builder.summary, loopPathIds, element.id)
        const filteredElementsLookup = dictPick(lookup.elements, relevantElementIds)

        const result = evaluateCalculation(
            element.formula,
            builder.summary.answers,
            builder.summary.calculations,
            filteredElementsLookup,
            lookup.subjectUnitData,
            loopPathIds,
            lookup.elementToStepLookup
        )
        const timestamp = calcutionTimestamp(
            element.formula,
            builder.summary.answers,
            builder.summary.calculations,
            loopPathIds,
            filteredElementsLookup,
            lookup.elementToStepLookup
        )
        builder.addCalculation(element, loopPathIds, result, timestamp || lookup.reportStartDate)
        showIfValue = result
    }

    //
    // Show Ifs

    if (showIfValue == null) return
    if (!hasShowIfs(element)) return

    for (const showIf of element.showIfs) {
        if (!isEqual(showIf.value, showIfValue)) continue

        for (const nestedElement of showIf.elements) {
            handleElement(builder, lookup, nestedElement, loopPathIds)
        }
    }
}

function getLatestParentIsUserSelectableAnswers(unitFilters: UnitFilter[], allAnswers: Flows.Answer[]): Flows.Answer[] {
    return unitFilters
        .filter((filter): filter is ParentIsUserSelectable => filter.type === 'ParentIsUserSelectable')
        .map((filter) => findLatestAnswer(allAnswers, filter.id as Flows.ElementId))
}

export function getLatestUnitFilterAnswers(
    unitFilters: UnitFilter[],
    allAnswers: Flows.Answer[]
): Flows.UnitSelectAnswer[] {
    const isAnswerOfTypeUnitSelect = (answer: Flows.Answer | null): answer is Flows.UnitSelectAnswer =>
        answer?.type === 'unit-select'

    return getLatestParentIsUserSelectableAnswers(unitFilters, allAnswers).filter(isAnswerOfTypeUnitSelect)
}

function findLatestAnswer(allAnswers: Flows.Answer[], elementId: Flows.ElementId): Flows.Answer | null {
    const answers = sortChronologicallyDescending(getElementAnswers(allAnswers, elementId, '' as LoopPath))
    return answers.length > 0 ? answers[0] : null
}

export function getElementAnswers(
    allAnswers: Flows.Answer[],
    elementId: Flows.ElementId,
    loopPath: LoopPath
): Flows.Answer[] {
    return allAnswers.filter((a) => a.element === elementId && getAnswerLoopPath(a) === loopPath)
}

function getAnswerToSelect(allAnswers: Flows.Answer[], element: Element, loopPathIds: LoopPathIds) {
    const loopPath = toLoopPath(loopPathIds)
    return getElementAnswers(allAnswers, element.id, loopPath)[0] ?? null
}

function findStepIndex(steps: Flows.Step[], id: Flows.StepId) {
    return steps.findIndex((step) => step.id === id)
}

function findStep(steps: Flows.Step[], id: Flows.StepId) {
    return steps[findStepIndex(steps, id)]
}

function hasFailures(sectionSummary: Flows.SectionSummary, loopPathIds: LoopPathIds) {
    return getSectionFailures(sectionSummary, sectionSummary.sectionId, loopPathIds).length > 0
}

export function getSectionFailures(
    sectionSummary: Flows.SectionSummary,
    sectionId: Flows.ElementId,
    loopPathIds: LoopPathIds
) {
    const loopPath = toLoopPath(loopPathIds)
    return sectionSummary.validationFailures.filter((f) => f.sectionId === sectionId && f.loopPath.startsWith(loopPath))
}

export function getElementFailures(
    sectionSummary: Flows.SectionSummary,
    elementId: Flows.ElementId,
    loopPathIds: LoopPathIds
) {
    const loopPath = toLoopPath(loopPathIds)
    return sectionSummary.validationFailures.filter((f) => f.elementId === elementId && f.loopPath === loopPath)
}

export function isVisible(flowSummary: Flows.FlowSummary, element: { id?: Flows.ElementId }, loopPathIds: LoopPathIds) {
    const loopPath = toLoopPath(loopPathIds)
    return flowSummary.elements.some((e) => e.elementId === element.id && e.loopPath === loopPath)
}

function getInProgressState(sectionSummary: Flows.SectionSummary) {
    return pipe(
        sectionSummary.answers,
        filter((answer) => !answer.automaticallyGenerated),
        every(answerIsEmpty),
        not
    )
}

export function getIsInProgress(summary: Flows.SectionSummary, elementId: Flows.ElementId, loopPathIds: LoopPathIds) {
    const loopPath = toLoopPath(loopPathIds)
    return summary.inProgressState.some((c) => c.elementId === elementId && c.loopPath === loopPath)
}

export function findCalculationResult(
    summary: Pick<Flows.SectionSummary, 'calculations'>,
    elementId: Flows.ElementId,
    loopPathIds: LoopPathIds
) {
    const loopPath = toLoopPath(loopPathIds)
    return summary.calculations.find((c) => c.elementId === elementId && c.loopPath === loopPath)?.result
}

export function hasStepInSummary(summary: Flows.SectionSummary, stepId: Flows.StepId, loopPathIds: LoopPathIds) {
    const loopPath = toLoopPath(loopPathIds)
    return summary.steps.some((s) => s.stepId === stepId && s.loopPath === loopPath)
}

export function hasElementInSummary(summary: Flows.FlowSummary, elementId: Flows.ElementId, loopPath?: LoopPath) {
    return summary.elements.some((e) => e.elementId === elementId && (isNil(loopPath) || e.loopPath === loopPath))
}

/**
 * When passing elements into calculation, we don't want to pass in any element that's not visible on the screen
 * And when in a loop, we only support including elements within the current loop iteration
 * We also exclude the current element if we've added it to the summary before making the calculation
 */
function getElementIdsForCalculation(
    summary: Flows.SectionSummary,
    loopPathIds: LoopPathIds,
    currentElementId?: Flows.ElementId
) {
    const loopPath = toLoopPath(loopPathIds)
    return pipe(
        summary.elements,
        filter((e) => e.loopPath === loopPath),
        map((e) => e.elementId),
        filter((elementId) => currentElementId !== elementId)
    )
}
